import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayers } from '@agissc/ktag-map';
import useAppState from '../../../AppStore';
import Scrollable from '../../../components/scrollable/Scrollable';
import SelectedTopicLayersPlaceholder from './SelectedTopicLayersPlaceholder';
import { useTracking } from '../../../utils/tracking/TrackingContext';
import RemoveButton from '../../../components/button/RemoveButton';
import { SortableList } from '../../../components/sortableList/SortableList';
import { SelectedTopicLayerItem } from './SelectedTopicLayerItem';

interface TopicLayerItem {
  id: string;
}

const SelectedTopicLayers = () => {
  const { t } = useTranslation('agisviewer');
  const { data, isLoading } = useLayers();
  const selectedTopicLayers = useAppState((state) => state.selectedTopicLayers);
  const setSelectedTopicLayers = useAppState((state) => state.setSelectedTopicLayers);
  const clearTopicLayers = useAppState((state) => state.clearTopicLayers);
  const topicLayerSettings = useAppState((state) => state.topicLayerSettings);
  const newAddedTopicLayer = useAppState((state) => state.newAddedTopicLayer);
  const errorLayers = useAppState((state) => state.errorLayers);
  const setTopicLayerOpacity = useAppState((state) => state.setTopicLayerOpacity);
  const setPreviewLayer = useAppState((state) => state.setPreviewLayer);
  const currentAttributeSearchTopicId = useAppState(
    (state) => state.currentAttributeSearchTopicId,
  );
  const setCurrentAttributeSearchTopicId = useAppState(
    (state) => state.setCurrentAttributeSearchTopicId,
  );

  useEffect(() => {
    setPreviewLayer(undefined);
  }, []);

  const removeSubTopicLayerSetting = useAppState(
    (state) => state.removeSubTopicLayerSetting,
  );
  const toggleTopicLayerVisibility = useAppState(
    (state) => state.toggleTopicLayerVisibility,
  );

  const { Tracking } = useTracking();

  if (!selectedTopicLayers.length) return null;
  if (isLoading) return <SelectedTopicLayersPlaceholder />;

  const topicLayerResult = data ?? {};
  const notAvailableTopicLayers = selectedTopicLayers.filter((id) => !data?.[id]);
  const items: TopicLayerItem[] = selectedTopicLayers.map((id) => ({ id }));

  const handleOrderChange = (_newItems: TopicLayerItem[], newIds: string[]) => {
    setSelectedTopicLayers(newIds, false);
  };

  const handleToggleTopicLayerVisibility = (topicId: string) => {
    const numberOfVisibleLayers = selectedTopicLayers.filter(
      (id) => topicLayerSettings[id]?.isVisible,
    ).length;
    Tracking.Topics.toggleTopicLayerVisibility(
      topicId,
      topicLayerSettings[topicId]?.isVisible ? 'invisible' : 'visible',
      topicLayerSettings[topicId]?.isVisible
        ? numberOfVisibleLayers - 1
        : numberOfVisibleLayers + 1,
    );
    toggleTopicLayerVisibility(topicId);
  };

  const handleRemoveTopicLayer = (topicId: string) => {
    Tracking.Topics.removeTopic(
      topicId,
      selectedTopicLayers.length - 1,
      'topics',
      'trashIcon',
    );

    if (currentAttributeSearchTopicId === topicId) {
      setCurrentAttributeSearchTopicId(null);
    }

    removeSubTopicLayerSetting(topicId);
  };

  const onRemove = () => {
    Tracking.Topics.removeAllTopics();
    clearTopicLayers();
  };

  return (
    <>
      <Scrollable>
        <div className="generic-list topic-layers">
          <SortableList
            items={items}
            onOrderChange={handleOrderChange}
            renderItem={(item, isDragOverlay) => (
              <SelectedTopicLayerItem
                id={item.id}
                topicLayer={topicLayerResult[item.id]}
                isNotAvailable={notAvailableTopicLayers.includes(item.id)}
                isHighlighted={newAddedTopicLayer === item.id}
                hasError={errorLayers.includes(item.id)}
                isVisible={topicLayerSettings[item.id]?.isVisible}
                opacity={topicLayerSettings[item.id]?.opacity}
                onRemove={() => handleRemoveTopicLayer(item.id)}
                onToggleVisibility={() => handleToggleTopicLayerVisibility(item.id)}
                onOpacityChanged={(value) => setTopicLayerOpacity(item.id, value)}
                isDragOverlay={isDragOverlay}
              />
            )}
          />
        </div>
      </Scrollable>

      <RemoveButton label={t('topic_all_remove')} onclick={onRemove} />
    </>
  );
};

export default SelectedTopicLayers;
